import { Link } from 'react-router-dom'
import { useEffect, useState, useRef } from 'react'
import { FaDraftingCompass, FaHardHat, FaWindowRestore, FaHome } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { motion, useMotionValue, useTransform, useAnimationFrame } from 'framer-motion'
import { getSubdomainUrl } from '../utils/links'

const HERO_VIDEO_SOURCES = [{ src: '/home/1-hero.mp4', type: 'video/mp4' }]
const BUSINESS_VIDEO_SOURCES = [{ src: '/home/2-learn-about-business-units.mp4', type: 'video/mp4' }]

const BUSINESS_UNITS = [
  { label: 'nav.architecture', subdomain: 'architecture', icon: FaDraftingCompass },
  { label: 'nav.construction', subdomain: 'construction', icon: FaHardHat },
  { label: 'nav.windows', subdomain: 'windows', icon: FaWindowRestore },
  { label: 'nav.roofing', subdomain: 'roofing', icon: FaHome },
]

// Carousel Config
const CAROUSEL_RADIUS_X = 350
const CAROUSEL_RADIUS_Z = 200
const ROTATION_SPEED = 0.15 // Increased speed for desktop

const HomeSection = () => {
  const [isLoaded, setIsLoaded] = useState(false)
  const [videoError, setVideoError] = useState({ hero: false, units: false })
  const { t } = useTranslation()

  // Carousel State
  const rotation = useMotionValue(0)
  const [isHovering, setIsHovering] = useState(false)
  const containerRef = useRef(null)

  // Auto-rotation (only runs if component is mounted/visible)
  useAnimationFrame(() => {
    if (!isHovering) {
      rotation.set(rotation.get() + ROTATION_SPEED)
    }
  })

  const [isSafari, setIsSafari] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 400)

    // Safari detection
    const ua = navigator.userAgent.toLowerCase()
    const isSafariUA = ua.includes('safari') && !ua.includes('chrome') && !ua.includes('chromium')
    setIsSafari(isSafariUA)

    return () => clearTimeout(timer)
  }, [])

  return (
    <section className="overflow-hidden relative min-h-screen text-white bg-black" aria-labelledby="business-units-heading" data-section="home">
      {/* --- HERO VIDEO BACKGROUND --- */}
      <div className="flex relative justify-center items-center min-h-screen">
        {!videoError.hero && (
          <video
            className="absolute inset-0 w-full h-full object-cover brightness-[0.4]"
            autoPlay
            loop
            muted
            playsInline
            preload="none"
            aria-hidden="true"
            onLoadedData={() => setIsLoaded(true)}
            onError={() => setVideoError((prev) => ({ ...prev, hero: true }))}
          >
            {HERO_VIDEO_SOURCES.map(({ src, type }) => (
              <source key={src} src={src} type={type} />
            ))}
            <track kind="captions" />
          </video>
        )}
        {videoError.hero && (
          <div className="flex absolute inset-0 justify-center items-center bg-black">
            <div className="text-center">
              <div className="mx-auto mb-6 w-16 h-16 rounded-full border border-home-title" />
              <p className="text-sm uppercase tracking-[0.3em] text-home-title">{t('home.video_unavailable')}</p>
            </div>
          </div>
        )}

        <div className="flex relative items-center min-h-screen">
          <div className="px-4 mx-auto -mt-24 max-w-5xl text-center sm:px-6 lg:px-8">
            <img
              src="/logo.webp"
              srcSet="/logo.webp 500w, /logo.webp 352w"
              sizes="(max-width: 640px) 15rem, (max-width: 768px) 20rem, 22rem"
              alt={t('home.logo_alt')}
              width="352"
              height="352"
              loading="eager"
              fetchPriority="high"
              className={`hero-logo-card mx-auto block w-60 sm:w-80 md:w-[22rem] ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
            />
          </div>
        </div>
      </div>

      {/* --- BUSINESS UNITS SECTION --- */}
      <div className="relative min-h-screen">
        {!videoError.units && (
          <video
            className="absolute inset-0 w-full h-full object-cover brightness-[0.25]"
            style={{ objectFit: 'cover' }}
            autoPlay
            loop
            muted
            playsInline
            preload="none"
            onLoadedData={() => setIsLoaded(true)}
            onError={() => setVideoError((prev) => ({ ...prev, units: true }))}
          >
            {BUSINESS_VIDEO_SOURCES.map(({ src, type }) => (
              <source key={src} src={src} type={type} />
            ))}
            <track kind="captions" />
          </video>
        )}
        {videoError.units && (
          <div className="flex absolute inset-0 justify-center items-center bg-black">
            <p className="text-sm uppercase tracking-[0.3em] text-home-title">{t('home.loading_units')}</p>
          </div>
        )}

        {/* Content Overlay */}
        <div className="flex overflow-hidden relative z-10 flex-col justify-center min-h-screen">
          <div className="flex flex-col items-center px-4 py-16 mx-auto w-full max-w-7xl">

            {/* Heading */}
            <div className="relative z-20 mb-12 text-center text-white lg:mb-20">
              <motion.h2
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="text-4xl font-black uppercase tracking-[0.25em] text-home-title sm:text-5xl lg:text-6xl"
              >
                {t('home.business_units_title')}
              </motion.h2>
            </div>

            {/* --- DESKTOP: 3D CAROUSEL (Hidden on mobile/tablet) --- */}
            <div
              ref={containerRef}
              className="hidden lg:flex relative w-full h-[400px] justify-center items-center perspective-1000"
              style={{ perspective: '1000px', transformStyle: 'preserve-3d' }}
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
            >
              {BUSINESS_UNITS.map((unit, index) => {
                return (
                  <CarouselCard
                    key={unit.label}
                    unit={unit}
                    index={index}
                    total={BUSINESS_UNITS.length}
                    rotation={rotation}
                    t={t}
                  />
                )
              })}
            </div>

            {/* --- MOBILE/TABLET: GRID GRID (Hidden on desktop) --- */}
            <div className="grid grid-cols-1 gap-6 w-full max-w-2xl lg:hidden sm:grid-cols-2">
              {BUSINESS_UNITS.map((unit) => {
                const Icon = unit.icon
                const url = getSubdomainUrl(unit.subdomain)
                const isExternal = url.startsWith('http')
                const Wrapper = isExternal ? 'a' : Link
                const props = isExternal ? { href: url, target: "_self" } : { to: url }

                return (
                  <Wrapper
                    key={unit.label}
                    {...props}
                    className="block"
                    aria-label={t(unit.label)}
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5 }}
                      className="flex h-48 flex-col items-center justify-center rounded-2xl border border-white/20 bg-white/5 backdrop-blur-sm active:scale-95 transition-all duration-200 hover:border-home-title/60 hover:bg-white/10 hover:shadow-[0_0_30px_rgba(255,255,255,0.1)]"
                    >
                      <div className="flex justify-center items-center mb-4 w-16 h-16 rounded-full border border-home-title bg-home-title/10 text-home-title">
                        {Icon ? <Icon className="w-7 h-7" /> : <span className="text-xl font-bold">JR</span>}
                      </div>
                      <p className="text-sm font-bold tracking-[0.2em] text-home-title uppercase text-center px-2">
                        {t(unit.label)}
                      </p>
                    </motion.div>
                  </Wrapper>
                )
              })}
            </div>



          </div>
        </div>
      </div>
    </section>
  )
}

// 3D Carousel Card Component
const CarouselCard = ({ unit, index, total, rotation, t }) => {
  const Icon = unit.icon
  const url = getSubdomainUrl(unit.subdomain)
  const isExternal = url.startsWith('http')
  const Wrapper = isExternal ? 'a' : Link
  const props = isExternal ? { href: url, target: "_self" } : { to: url }

  // Each card is offset by specific angle
  const offset = (360 / total) * index

  // Transform Angle to Radians for Math
  const angle = useTransform(rotation, (r) => {
    const currentAngle = (r + offset) % 360
    return (currentAngle * Math.PI) / 180
  })

  // Calculate X and Z positions
  const x = useTransform(angle, (a) => Math.sin(a) * CAROUSEL_RADIUS_X)
  const z = useTransform(angle, (a) => Math.cos(a) * CAROUSEL_RADIUS_Z)

  // Calculate visual properties based on Z (depth)
  const scale = useTransform(angle, (a) => {
    const cos = Math.cos(a)
    return 0.6 + (cos + 1) / 2 * 0.5
  })

  const opacity = useTransform(angle, (a) => {
    const cos = Math.cos(a)
    return 0.4 + (cos + 1) / 2 * 0.6
  })

  const zIndex = useTransform(angle, (a) => {
    return Math.round(Math.cos(a) * 100)
  })

  return (
    <motion.div
      style={{
        x,
        z,
        scale,
        opacity,
        zIndex,
        position: 'absolute',
      }}
      className="origin-center"
    >
      <Wrapper
        {...props}
        className="block relative cursor-pointer group"
        aria-label={t(unit.label)}
      >
        <div className="flex h-56 w-80 flex-col items-center justify-center rounded-2xl border border-white/20 bg-white/5 backdrop-blur-md transition-all duration-300 group-hover:border-home-title/60 group-hover:bg-white/10 group-hover:shadow-[0_0_30px_rgba(255,255,255,0.1)]">
          <div className="flex justify-center items-center mb-4 w-16 h-16 rounded-full border border-home-title bg-home-title/10 text-home-title">
            {Icon ? <Icon className="w-7 h-7" /> : <span className="text-xl font-bold">JR</span>}
          </div>
          <p className="text-lg font-bold tracking-[0.2em] text-home-title uppercase text-center px-4">
            {t(unit.label)}
          </p>

        </div>
      </Wrapper>
    </motion.div>
  )
}

export default HomeSection
